import { useEffect, useRef, useState } from 'react'
import { useTranslations } from 'use-intl'

import { BookDemoButton } from '@/components/cta-buttons'
import { Button } from '@/components/ui/button'
import { HeroShader } from '@/components/ui/hero-shader'
import { InlineVideo } from '@/components/inline-video'
import { COMPLIASSIST_VIDEO } from '@/features/solution-page/data/features'
import { useSectionView } from '@/lib/analytics/use-section-view'

/** Anchor of the apps section, target of the hero's secondary button. */
export const SOLUTIONS_ANCHOR = 'solutions'

/**
 * Vertical word roller: cycles through `words` one line at a time, forever, in
 * one direction. The first word (index 0) is what SSR paints, so the LCP
 * headline stays correct on first render; the roll only starts after hydration.
 *
 * Seamless loop: the first word is duplicated at the end of the stack, so after
 * the last word it slides *forward* onto the duplicate, then we snap the offset
 * back to 0 with the transition disabled — visually identical, no rewind.
 *
 * The extra `leading` gives each word a vertical gutter so a neighbour's
 * descender (the "p" in protection) or accent never bleeds past the clip edge.
 *
 * The headline is centred, so the clip box animates to the current word's
 * width: the line re-centres around short words ("sûreté") instead of leaving
 * a gap sized for the longest one. Until the first measurement (server
 * render, no JS) the other words are taken out of flow, so the box already
 * fits the first word and hydration doesn't shift the line.
 */
function RollingWord({ words }: { words: string[] }) {
  const [index, setIndex] = useState(0)
  const [animate, setAnimate] = useState(true)
  const [width, setWidth] = useState<number>()
  const stackRef = useRef<HTMLSpanElement>(null)
  const loop = [...words, words[0]]

  useEffect(() => {
    // 3s per word: the slide takes 0.7s, leaving ~2.3s to read each one.
    const id = setInterval(() => setIndex((i) => i + 1), 3000)
    return () => clearInterval(id)
  }, [])

  // Landed on the duplicated first word → snap back to the real one, no anim.
  useEffect(() => {
    if (index !== words.length) return
    const id = setTimeout(() => {
      setAnimate(false)
      setIndex(0)
    }, 720)
    return () => clearTimeout(id)
  }, [index, words.length])

  // Re-arm the transition once the snap has painted.
  useEffect(() => {
    if (animate) return
    const id = setTimeout(() => setAnimate(true), 20)
    return () => clearTimeout(id)
  }, [animate])

  // Measure the current word. A ResizeObserver rather than a one-off read: it
  // fires on observe, again once the web font swaps in, and when a breakpoint
  // changes the font size — each of which changes the word's width.
  useEffect(() => {
    const stack = stackRef.current
    const word = stack?.children[index]
    if (!stack || !(word instanceof HTMLElement)) return
    const observer = new ResizeObserver(() => setWidth(word.offsetWidth))
    observer.observe(stack)
    return () => observer.disconnect()
  }, [index])

  // The snap back to the first word must not animate at all. Dropping only the
  // duration isn't enough: `transition-*` alone falls back to Tailwind's 150ms
  // default and the stack visibly rewinds through every word.
  const timing = 'duration-700 ease-in-out motion-reduce:transition-none'
  const widthTransition = animate ? `transition-[width] ${timing}` : ''
  const stackTransition = animate ? `transition-transform ${timing}` : ''
  return (
    // Decorative: the roller stacks every synonym (plus a loop-duplicate) as live
    // text nodes, so the H1's accessible name comes from the parent's aria-label.
    <span
      aria-hidden
      className={`ms-[0.25em] inline-block h-[1lh] overflow-hidden align-[-0.35em] leading-[1.4] text-primary ${widthTransition}`}
      style={{ width }}
    >
      <span
        ref={stackRef}
        className={`relative flex flex-col items-start ${stackTransition}`}
        style={{ transform: `translateY(-${index}lh)` }}
      >
        {loop.map((word, i) => (
          <span
            key={i}
            className={`block h-[1lh] w-max ${width === undefined && i > 0 ? 'invisible absolute' : ''}`}
          >
            {word}
          </span>
        ))}
      </span>
    </span>
  )
}

/**
 * Frame holding the CompliAssist motion video, as on its feature page:
 * thumbnail + play button, the video only loads, and plays in place, on
 * click. Shown on every locale, even though its copy is burned in French.
 *
 * No drop shadow and only a faint translucent rim: a blurred copy of the
 * thumbnail behind the frame spills its blue into the shader, so the frame
 * reads as lighting the hero rather than a card laid on it. The blur is
 * static (never animated) and smaller on phones, where the frame is too.
 *
 * The mascot peeks over the frame's top edge, paws gripping it: the art's
 * flat body bottom sits 91% down the image, so lifting it by 91% of its own
 * height lands that line on the edge and lets the paws hang over the frame.
 */
function HeroFrame() {
  const t = useTranslations('home.hero')
  return (
    <div className="relative isolate rounded-[1.375rem] border border-white/35 bg-white/10 p-1">
      <img
        src={COMPLIASSIST_VIDEO.thumbnail.src}
        alt=""
        aria-hidden
        loading="lazy"
        decoding="async"
        className="pointer-events-none absolute inset-[6%] -z-10 size-[88%] translate-y-[8%] scale-[1.08] rounded-[2.5rem] opacity-85 blur-[40px] saturate-130 select-none md:blur-[70px]"
      />
      <InlineVideo
        title={t('videoTitle')}
        thumbnail={COMPLIASSIST_VIDEO.thumbnail}
        src={COMPLIASSIST_VIDEO.src}
        srcMobile={COMPLIASSIST_VIDEO.srcMobile}
        className="rounded-[1.125rem]"
      />
      {/* After the video so it paints over it; click-through so the paws
          never steal the thumbnail's play click. */}
      <img
        src="/mascot/mascot-grip.webp"
        alt=""
        aria-hidden
        width={827}
        height={374}
        decoding="async"
        className="pointer-events-none absolute top-0 left-1/2 w-48 -translate-x-1/2 -translate-y-[79%] select-none sm:w-60 lg:w-[21.5rem]"
      />
    </div>
  )
}

export function HeroSection() {
  const t = useTranslations('home')
  const sectionViewRef = useSectionView('home', 'hero')
  const words = t('hero.titleWords')
    .split(',')
    .map((w) => w.trim())
  // Clean, single-sentence accessible name for the H1. The animated roller marks
  // itself aria-hidden, so this label (built from the primary word) is the one
  // canonical heading assistive tech and search/AI extractors read — instead of
  // the run-on synonym pile the stacked word-nodes produce in raw text.
  const headline = `${t('hero.titlePrefix')} ${words[0]} ${t('hero.titleLine2')} ${t('hero.titleEnd')}`
  return (
    // Full-bleed hero pulled up behind the transparent sticky header.
    <section
      ref={sectionViewRef}
      data-testid="home-hero"
      // `overflow-x-clip`, not `overflow-hidden`: the frame's halo runs well
      // past the section bottom and must fade out over the logos section,
      // not stop on a hard line at the hero's edge.
      className="relative isolate -mt-(--header-h) overflow-x-clip"
    >
      {/* Backdrop: the shader (blurs into view once its first frame is drawn),
          then a white veil easing the bottom into the logos section. The
          shader gets its own clipping box: its entrance scales and blurs it
          past the section, and the section itself only clips horizontally
          (the frame shadow must overflow), so it would show below the veil. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 overflow-hidden"
      >
        <HeroShader className="size-full" />
      </div>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 -z-10 h-[30%]"
        style={{
          background:
            'linear-gradient(to bottom, rgb(255 255 255 / 0) 0%, rgb(255 255 255 / 0.12) 30%, rgb(255 255 255 / 0.45) 60%, rgb(255 255 255 / 0.85) 85%, rgb(255 255 255) 100%)',
        }}
      />
      {/* CSS entrance (`.reveal-mount`), not a JS reveal: the hero is the LCP
          and above the fold, so its text must paint on first render, never
          wait for hydration. The staggered fade-in runs from CSS at paint. */}
      <div className="reveal-mount mx-auto flex w-full max-w-7xl flex-col items-center px-6 pt-[calc(var(--header-h)+3.5rem)] pb-14 text-center lg:px-8 lg:pt-[calc(var(--header-h)+6.5rem)] lg:pb-20">
        <h1
          aria-label={headline}
          className="font-serif text-4xl font-semibold tracking-tight text-accent-foreground sm:text-5xl lg:text-6xl [&>span]:leading-[1.12]"
        >
          <span className="block">
            {t('hero.titlePrefix')}
            <RollingWord words={words} />{' '}
            {/* Own line on phones: the roller's width changes would otherwise
                re-wrap the sentence every two seconds. */}
            <span className="block sm:inline">{t('hero.titleLine2')}</span>
          </span>
          <span className="block text-primary">{t('hero.titleEnd')}</span>
        </h1>
        <p className="mt-5 max-w-xl font-mono text-base font-normal text-accent-foreground lg:text-lg">
          {t('hero.subtitle')}
        </p>
        <div className="mt-8 flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
          <BookDemoButton source="hero" />
          <Button variant="outline" asChild className="bg-background/60">
            <a
              href={`#${SOLUTIONS_ANCHOR}`}
              onClick={(event) => {
                const target = document.getElementById(SOLUTIONS_ANCHOR)
                if (!target) return
                event.preventDefault()
                const reduce = window.matchMedia(
                  '(prefers-reduced-motion: reduce)',
                ).matches
                target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth' })
              }}
            >
              {t('hero.secondaryCta')}
            </a>
          </Button>
        </div>
        {/* Gap sized to the mascot's head above the frame edge (~0.37 × its
            width), so it never reaches the buttons. */}
        <div className="mt-28 w-full max-w-[66rem] sm:mt-32 lg:mt-40">
          <HeroFrame />
        </div>
      </div>
    </section>
  )
}
